import { useEffect, useState } from 'react';
import classNames from 'classnames';
import { DateTime } from 'luxon';
import { useLocalesAPI } from '../../../clients/quipli-service/locales/locales-api-v1';
import { useAPI } from '../../../clients/quipli-service/quipli-service-client';
import { User } from '../../../clients/quipli-service/user/user';
import { StateProvince } from '../../../clients/quipli-service/locales/locales';
import { useLicenseScan } from '../hooks/use-license-scan';
import { Button } from '../../../components/form/button';
import { DatePicker } from '../../../components/date-picker/date-picker';
import { Field, FileField } from '../../../components/field/field';
import { SelectForm } from '../../../components/select-form/select-form';
import { Spinner } from '../../../components/spinner/spinner';
import { toast } from '../../../util/toasts';
import { Typography } from '../../../components/typography/typography';
import { ValidationError } from '../../../components/validation-error/validation-error';

export const LicenseScanFormV1 = ({
  userScanSuccessful,
  userScanFailed,
  closeModal,
  user,
}: {
  userScanSuccessful: (user: User) => void;
  userScanFailed: () => void;
  closeModal: () => void;
  user?: User;
}) => {
  const { getConfiguration } = useAPI();
  const { searchStateProvinces } = useLocalesAPI();

  const [errors, setErrors] = useState<string[]>([]);
  const [isRequestInProgress, setIsRequestInProgress] = useState<boolean>(false);

  const [fullName, setFullName] = useState<string>('');
  const [dateOfBirth, setDateOfBirth] = useState<DateTime | undefined>();
  const [licenseNumber, setLicenseNumber] = useState<string>('');
  const [expirationDate, setExpirationDate] = useState<DateTime | undefined>();
  const [stateProvince, setStateProvince] = useState<StateProvince | undefined>();
  const [file, setFile] = useState<File>();

  const [stateProvinces, setStateProvinces] = useState<StateProvince[] | undefined>();

  const pageTitleKey = ' - [SCANNING]';

  const { submitLicenseData } = useLicenseScan();

  useEffect(() => {
    const loadLocales = async () => {
      try {
        const countryConfig = await getConfiguration('CONF_COUNTRY');
        if (!countryConfig) {
          throw new Error('Failed to load country configuration.');
        }

        const states = await searchStateProvinces({ countryIds: [parseInt(countryConfig.value)] });
        setStateProvinces(states);
      } catch (e: any) {
        toast('Failed to load state/provinces.', 'error');
      }
    };

    loadLocales();

    // Add the title key to the page title so ParseLink selects the correct tab
    if (!document.title.includes(pageTitleKey)) {
      document.title = document.title + pageTitleKey;
    }
  }, []);

  const isSubmitDisabled = (): boolean => {
    return (
      fullName === '' ||
      dateOfBirth === undefined ||
      licenseNumber === '' ||
      expirationDate === undefined ||
      stateProvince === undefined ||
      file === undefined
    );
  };

  const submitRequest = async () => {
    // Validate inputs
    const errors: string[] = validateInputs();
    if (errors.length > 0) {
      setErrors(errors);
      return;
    }

    // Block buttons and exit of modal
    setIsRequestInProgress(true);

    const { user: updatedUser, error } = await submitLicenseData(
      user,
      fullName,
      dateOfBirth!,
      licenseNumber,
      expirationDate!,
      stateProvince!,
      file,
    );

    if (updatedUser) {
      userScanSuccessful(updatedUser);
      return;
    } else if (error) {
      toast(error, 'error');
    }

    setIsRequestInProgress(false);

    // Reset fields and call failed callback
    resetTitle();
    resetFields();
    userScanFailed();
  };

  const validateInputs = () => {
    const errors: string[] = [];

    if (dateOfBirth !== undefined && dateOfBirth > DateTime.now()) {
      errors.push('Date of birth is in the future.');
    }

    if (expirationDate !== undefined && expirationDate < DateTime.now()) {
      errors.push('Expiration date is in the past.');
    }

    return errors;
  };

  const onCloseModal = () => {
    resetTitle();
    resetFields();
    closeModal();
  };

  const resetTitle = () => {
    // Remove the title key from the page title
    if (document.title.includes(pageTitleKey)) {
      document.title = document.title.replace(pageTitleKey, '');
    }
  };

  const resetFields = () => {
    setFullName('');
    setDateOfBirth(undefined);
    setLicenseNumber('');
    setExpirationDate(undefined);
    setStateProvince(undefined);
    setFile(undefined);
    setErrors([]);
  };

  return (
    <div className="w-[350px]">
      <div className="text-center px-2 mb-4">
        <Typography type="text-sm" className="text-grey-500">
          Insert the driver's license into the scanner, and the scanner will automatically populate the fields below.
        </Typography>
      </div>

      <ValidationError className={classNames({ hidden: errors.length === 0 }, 'mb-2')}>
        {errors.map((error, i) => (
          <span key={i}>{error}</span>
        ))}
      </ValidationError>

      <Field
        type="text"
        label="Full Name"
        id="fullName"
        className="mb-2"
        value={fullName}
        onChange={setFullName}
        isRequired={true}
        autofocus={true}
      />

      <DatePicker
        label="Date of Birth"
        inputWrapperClassName="mb-2"
        isRequired={true}
        value={dateOfBirth}
        onChange={setDateOfBirth}
      />

      <Field
        type="text"
        label="License Number"
        id="licenseNumber"
        className="mb-2"
        value={licenseNumber}
        onChange={setLicenseNumber}
        isRequired={true}
      />

      <DatePicker
        label="Expiration Date"
        inputWrapperClassName="mb-2"
        isRequired={true}
        value={expirationDate}
        onChange={setExpirationDate}
      />

      <SelectForm
        label="Issuing State/Province"
        id="stateProvince"
        className="mb-2"
        menuShouldBlockScroll
        menuPosition="fixed"
        menuPortalTarget={document.body}
        options={stateProvinces}
        value={stateProvince}
        onChange={(option) => {
          setStateProvince(option ?? undefined);
        }}
        getOptionLabel={(o) => o.name}
        getOptionValue={(o) => `${o.id}`}
        isRequired={true}
        isDisabled={!stateProvinces}
        isLoading={!stateProvinces}
      />

      <FileField
        label="Front Image"
        id="file"
        className="mb-4"
        onChange={(file) => setFile(file as File)}
        value={file}
        isRequired={true}
      />

      {isRequestInProgress ? (
        <Spinner />
      ) : (
        <div className="flex flex-col lg:flex-row [&>*]:!grow gap-3">
          <Button className="button button-secondary" onClick={() => onCloseModal()}>
            Cancel
          </Button>

          <Button className="button button-primary" id="submit" disabled={isSubmitDisabled()} onClick={submitRequest}>
            Save
          </Button>
        </div>
      )}
    </div>
  );
};
